import React, { useContext, useEffect, useState } from "react";
import axiosInstance from "api/axiosInstance";
import { toast } from "react-toastify";
import { Box, Switch, Dialog, DialogTitle, DialogContent, Typography } from "@mui/material";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { COLORS } from "theme/tokens";
import { fmtDate } from "utils/datetime";
import { LanguageContext } from "../../context/LanguageContext";

/**
 * 共用：自動續費開關（方案管理 + 帳務管理共用，功能/狀態一致）
 *  - 狀態來源：myPlan.autoRenewEnabled（呼叫端傳入，呼叫端負責 fetch /plans/me/plan）
 *  - 切換：POST /users/subscription/auto-renew { enabled }；變更後呼叫 onChanged() 讓呼叫端重抓
 *  - 規則：試用/免費 → 引導升級；年繳 → 說明彈窗；關閉 → 確認彈窗；開啟遇金流已取消 → 重新訂閱彈窗
 *  - 2026-09-28 月繳期滿：myPlan.contract.isFinalPeriod（綠界定期定額最後一期，舊單 12 期）→ 不顯示開關，
 *    改顯示「月繳訂閱將於 X 期滿（共 N 期）」＋「立即重新訂閱」（開重新訂閱彈窗的期滿版文案）；
 *    開啟時後端回 CONTRACT_ENDING 也開同一個彈窗
 *
 * @param {object}   myPlan    /plans/me/plan 回傳物件（需含 autoRenewEnabled / planCode / billingCycle）
 * @param {function} onChanged 變更成功後的 callback（通常 = 呼叫端的 fetchMyPlan）
 * @param {object}   labelSx   可選：覆寫「自動續費」文字樣式以配合各頁外觀
 */
export default function AutoRenewToggle({ myPlan, onChanged, labelSx }) {
  const { componentLanguageData, loadLanguageFile, language } = useContext(LanguageContext);
  useEffect(() => {
    loadLanguageFile("AutoRenewToggle", "common/AutoRenewToggle/AutoRenewToggle_lang");
  }, [language, loadLanguageFile]);
  const L = (componentLanguageData.AutoRenewToggle || { Main: {} }).Main;

  const [autoRenewBusy, setAutoRenewBusy] = useState(false);
  const [autoRenewOffOpen, setAutoRenewOffOpen] = useState(false);
  const [resubscribeOpen, setResubscribeOpen] = useState(false);
  const [resubMode, setResubMode] = useState("canceled"); // canceled＝綠界已取消／contract＝月繳期滿（最後一期）
  const [yearlyNoticeOpen, setYearlyNoticeOpen] = useState(false);

  const autoRenewActive = !!myPlan?.autoRenewEnabled;
  const contract = myPlan?.contract || null;
  const isFinalPeriod = !!contract?.isFinalPeriod;
  const openResubscribe = (mode) => {
    setResubMode(mode);
    setResubscribeOpen(true);
  };

  const handleAutoRenewChange = async (next) => {
    // 試用/免費方案沒有可續訂的訂閱 → 引導升級（用 planCode 判斷，hasActiveSubscription 對試用可能誤判）
    if (["STARTER", "FREE", "FREE_TRIAL", "TRIAL"].includes(myPlan?.planCode)) {
      toast.info(L.AutoRenewRequireUpgrade || "如要開啟自動續費，請先升級為付費會員方案。");
      return;
    }
    // 年繳為一次性付款、無 recurring mandate，無法開啟月繳自動續費 → 跳說明彈窗
    if (next && myPlan?.billingCycle === "yearly") {
      setYearlyNoticeOpen(true);
      return;
    }
    if (!next) {
      setAutoRenewOffOpen(true);
      return;
    }
    setAutoRenewBusy(true);
    try {
      await axiosInstance.post("/users/subscription/auto-renew", { enabled: true });
      onChanged?.();
      toast.success(L.AutoRenewOnToast || "已開啟自動續費");
    } catch (err) {
      const code = err.response?.data?.code;
      if (code === "ALREADY_CANCELED_AT_GATEWAY") {
        openResubscribe("canceled");
      } else if (code === "CONTRACT_ENDING") {
        openResubscribe("contract");
      } else {
        toast.error(L.AutoRenewError || "設定失敗，請稍後再試");
      }
    } finally {
      setAutoRenewBusy(false);
    }
  };

  // 立即重新訂閱（複用升級訂閱金流，webhook 退舊單剩餘 Coin）
  const confirmResubscribe = async () => {
    setAutoRenewBusy(true);
    try {
      const res = await axiosInstance.post("/plans/upgrade-subscribe", { planCode: myPlan?.planCode });
      const popup = window.open();
      if (popup) popup.document.write(res.data);
      setResubscribeOpen(false);
    } catch (err) {
      toast.error(L.AutoRenewError || "設定失敗，請稍後再試");
    } finally {
      setAutoRenewBusy(false);
    }
  };

  const confirmTurnOffAutoRenew = async () => {
    setAutoRenewBusy(true);
    try {
      await axiosInstance.post("/users/subscription/auto-renew", { enabled: false });
      onChanged?.();
      setAutoRenewOffOpen(false);
      toast.success(L.AutoRenewOffToast || "已關閉自動續費");
    } catch (err) {
      toast.error(L.AutoRenewError || "設定失敗，請稍後再試");
    } finally {
      setAutoRenewBusy(false);
    }
  };

  return (
    <>
      {isFinalPeriod ? (
        // 月繳期滿（最後一期）：開關開了也不會再扣款 → 顯示期滿日＋立即重新訂閱
        <Box>
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <Typography sx={{ fontSize: "0.875rem", color: "text.secondary", ...labelSx }}>
              {(L.ContractEndLabel || "月繳訂閱將於 {date} 期滿（共 {n} 期）")
                .replace("{date}", fmtDate(contract.endsAt))
                .replace("{n}", String(contract.execTimes ?? "-"))}
            </Typography>
            <MDButton
              size="small"
              onClick={() => openResubscribe("contract")}
              disabled={autoRenewBusy}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                minHeight: 0,
                py: 0.5,
                background: "linear-gradient(135deg, #ff8a3d 0%, #ffb347 100%)",
                color: "#fff !important",
                fontWeight: 600,
                "&:hover": { background: "linear-gradient(135deg, #ff7a25 0%, #ffa030 100%)" },
              }}
            >
              {L.ContractResubBtn || "立即重新訂閱"}
            </MDButton>
          </Box>
          <Typography sx={{ fontSize: "0.75rem", color: "text.secondary", mt: 0.5, lineHeight: 1.6 }}>
            {L.ContractEndHint || "這份訂閱的期數已全部扣完，期滿後不會再自動扣款、方案會切換為免費方案。如要繼續使用，請重新訂閱。"}
          </Typography>
        </Box>
      ) : (
        <Box display="flex" alignItems="center" gap={1}>
          <Typography sx={{ fontSize: "0.875rem", color: "text.secondary", ...labelSx }}>
            {L.AutoRenew || "自動續費"}
          </Typography>
          <Switch
            checked={autoRenewActive}
            disabled={autoRenewBusy || !myPlan}
            onChange={(e) => handleAutoRenewChange(e.target.checked)}
            size="small"
            sx={{
              "& .MuiSwitch-switchBase.Mui-checked": { color: COLORS.main },
              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                backgroundColor: COLORS.main,
              },
            }}
          />
        </Box>
      )}

      {/* === 關閉自動續費確認 === */}
      <Dialog
        open={autoRenewOffOpen}
        onClose={() => !autoRenewBusy && setAutoRenewOffOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: 20 }}>
          {L.AutoRenewOffTitle || "關閉自動續費"}
        </DialogTitle>
        <DialogContent dividers>
          <MDTypography sx={{ fontSize: 16, fontWeight: 700, color: COLORS.black, mb: 1 }}>
            {L.AutoRenewOffConfirmTitle || "確認關閉自動續費"}
          </MDTypography>
          <MDTypography sx={{ fontSize: 14, color: COLORS.main, lineHeight: 1.7 }}>
            {L.AutoRenewOffDesc ||
              "關閉後不會立即失效，方案會用到剩餘時間結束，之後自動降為免費方案。確定關閉？"}
          </MDTypography>
        </DialogContent>
        <Box display="flex" justifyContent="flex-end" gap={1.5} px={3} py={2}>
          <MDButton
            variant="outlined"
            color="secondary"
            onClick={() => setAutoRenewOffOpen(false)}
            disabled={autoRenewBusy}
            sx={{ borderRadius: 2, textTransform: "none" }}
          >
            {L.Cancel || "取消"}
          </MDButton>
          <MDButton
            onClick={confirmTurnOffAutoRenew}
            disabled={autoRenewBusy}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              background: "linear-gradient(135deg, #ff8a3d 0%, #ffb347 100%)",
              color: "#fff !important",
              fontWeight: 600,
              "&:hover": { background: "linear-gradient(135deg, #ff7a25 0%, #ffa030 100%)" },
            }}
          >
            {autoRenewBusy ? (L.Processing || "處理中...") : (L.AutoRenewOffConfirm || "確認關閉")}
          </MDButton>
        </Box>
      </Dialog>

      {/* === 立即重新訂閱（綠界已取消、無法無縫恢復時；或月繳期滿的最後一期）=== */}
      <Dialog
        open={resubscribeOpen}
        onClose={() => !autoRenewBusy && setResubscribeOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: 20 }}>
          {resubMode === "contract"
            ? (L.ContractResubTitle || "重新訂閱以繼續使用")
            : (L.ResubTitle || "重新訂閱以開啟自動續費")}
        </DialogTitle>
        <DialogContent dividers>
          <MDTypography sx={{ fontSize: 14, color: COLORS.black, lineHeight: 1.8 }}>
            {resubMode === "contract"
              ? (L.ContractResubDesc ||
                "您目前的月繳訂閱已是最後一期，期滿後不會再自動扣款。現在重新訂閱需完成付款：新訂閱立即生效、每月自動扣款直到您取消；目前方案剩餘的天數會換算成等值的 MAS Coin 回饋到您的帳戶，已付費的部分不會損失。您也可以等期滿、方案切換為免費方案後，再到方案頁重新訂閱。")
              : (L.ResubDesc ||
                "您的訂閱已進入取消程序，無法直接恢復自動續費。若要繼續使用，需要重新訂閱並完成付款。您目前方案剩餘的天數將換算成等值的 MAS Coin 回饋到您的帳戶，已付費的部分不會損失。完成付款後新訂閱立即生效並每月自動扣款。")}
          </MDTypography>
        </DialogContent>
        <Box display="flex" justifyContent="flex-end" gap={1.5} px={3} py={2}>
          <MDButton
            variant="outlined"
            color="secondary"
            onClick={() => setResubscribeOpen(false)}
            disabled={autoRenewBusy}
            sx={{ borderRadius: 2, textTransform: "none" }}
          >
            {L.Cancel || "取消"}
          </MDButton>
          <MDButton
            onClick={confirmResubscribe}
            disabled={autoRenewBusy}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              background: "linear-gradient(135deg, #ff8a3d 0%, #ffb347 100%)",
              color: "#fff !important",
              fontWeight: 600,
              "&:hover": { background: "linear-gradient(135deg, #ff7a25 0%, #ffa030 100%)" },
            }}
          >
            {autoRenewBusy ? (L.Processing || "處理中...") : (L.ResubConfirm || "立即重新訂閱")}
          </MDButton>
        </Box>
      </Dialog>

      {/* === 年繳方案無法開啟自動續費 === */}
      <Dialog
        open={yearlyNoticeOpen}
        onClose={() => setYearlyNoticeOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: 20 }}>
          {L.AutoRenewYearlyTitle || "年繳方案無法開啟自動續費"}
        </DialogTitle>
        <DialogContent dividers>
          <MDTypography sx={{ fontSize: 14, color: COLORS.black, lineHeight: 1.8 }}>
            {L.AutoRenewYearlyNotice ||
              "您目前是年繳方案，無法開啟月繳自動續費。年繳於到期前持續有效，到期後可再次購買續約。"}
          </MDTypography>
        </DialogContent>
        <Box display="flex" justifyContent="flex-end" px={3} py={2}>
          <MDButton
            onClick={() => setYearlyNoticeOpen(false)}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              background: "linear-gradient(135deg, #ff8a3d 0%, #ffb347 100%)",
              color: "#fff !important",
              fontWeight: 600,
              "&:hover": { background: "linear-gradient(135deg, #ff7a25 0%, #ffa030 100%)" },
            }}
          >
            {L.AutoRenewYearlyBtn || "我知道了"}
          </MDButton>
        </Box>
      </Dialog>
    </>
  );
}
